import { createContext, useContext, useState, ReactNode } from 'react';
import { Tool } from '@/types/database';

interface CompareContextType {
  selectedTools: Tool[];
  addTool: (tool: Tool) => void;
  removeTool: (toolId: string) => void;
  clearAll: () => void;
  isSelected: (toolId: string) => boolean;
  canAdd: boolean;
}

const CompareContext = createContext<CompareContextType | undefined>(undefined);

const MAX_COMPARE_TOOLS = 4;

export function CompareProvider({ children }: { children: ReactNode }) {
  const [selectedTools, setSelectedTools] = useState<Tool[]>([]);

  const addTool = (tool: Tool) => {
    if (selectedTools.length < MAX_COMPARE_TOOLS && !isSelected(tool.id)) {
      setSelectedTools(prev => [...prev, tool]);
    }
  };

  const removeTool = (toolId: string) => {
    setSelectedTools(prev => prev.filter(t => t.id !== toolId));
  };

  const clearAll = () => {
    setSelectedTools([]);
  };

  const isSelected = (toolId: string) => {
    return selectedTools.some(t => t.id === toolId);
  };

  const canAdd = selectedTools.length < MAX_COMPARE_TOOLS;

  return (
    <CompareContext.Provider value={{ selectedTools, addTool, removeTool, clearAll, isSelected, canAdd }}>
      {children}
    </CompareContext.Provider>
  );
}

export function useCompare() {
  const context = useContext(CompareContext);
  if (context === undefined) {
    throw new Error('useCompare must be used within a CompareProvider');
  }
  return context;
}
